import type { IListingCardData, IListingListData, ListingListState } from './types'
import type { Selector, AppState } from 'store'

import { createSelector } from '@reduxjs/toolkit'
import { generateAddress } from '@rentspree/helper'
import isEmpty from 'lodash/isEmpty'

import env from 'env'

import { initialState } from './initial-state'

export const selectListingListState: Selector<ListingListState> = (state: AppState) =>
  state.listingList ?? initialState

export const selectListingListData: Selector<IListingListData> = createSelector(
  selectListingListState,
  (listingState: ListingListState) => {
    if (isEmpty(listingState.data)) return initialState.data
    return listingState.data
  },
)

export const selectIsListingListExist: Selector<boolean> = createSelector(
  selectListingListData,
  (listingListData: IListingListData) => listingListData.data.length > 0,
)

export const selectListingListFetchState: Selector<RequestStatus> = createSelector(
  selectListingListState,
  (listingState: ListingListState) => listingState.status || 'initial',
)

export const selectListingListForListingCard: Selector<IListingCardData[]> = createSelector(
  selectListingListData,
  (listingListData: IListingListData) =>
    listingListData.data.map((listing: IPublicListing) => {
      const {
        id,
        leasingInformation,
        addressDetails,
        propertyDetails,
        slug = '',
        information,
      } = listing
      const { monthlyRent } = leasingInformation.pricingDetails || {}
      const { bathrooms, bedrooms, propertyType, squareFeet } = propertyDetails
      const { photos = [] } = information || {}
      const publicListingUrl = `${env.BASE_URL}/listing/${slug}`
      const propertyDetail = `${bedrooms} Bd | ${bathrooms} Ba${
        squareFeet ? ` | ${squareFeet} Sqft.` : ''
      }`
      const neededListingData: IListingCardData = {
        id,
        rentAmount: monthlyRent,
        propertyAddress: generateAddress(addressDetails) ?? '',
        propertyType,
        propertyDetail,
        photo: photos?.[0]?.src || '',
        publicListingUrl,
        meta: {
          propertyAddress: addressDetails,
          bathrooms,
          bedrooms,
          squareFeet,
        },
      }
      return neededListingData
    }),
)

export const selectListingListPagination: Selector<IListingListData['pagination']> = createSelector(
  selectListingListData,
  (listingListData: IListingListData) => listingListData.pagination,
)
